import { zodResolver } from "@hookform/resolvers/zod";
import { container_classname } from "@components/new-ui/constants";
import useRecaptcha from "@hooks/useRecaptcha";
import { getUtmAndReferrer, SourceInformation } from "@utils/campaign-tracker";
import { errMsgFocusStyle, verifyPhoneNumber } from "@utils/form";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import IntlTelInput, { CountryData } from "react-intl-tel-input";
import "react-intl-tel-input/dist/main.css";
import { Controller, SubmitHandler, UseFormRegisterReturn, useFieldArray, useForm } from "react-hook-form";
import { FiChevronDown, FiPlus, FiTrash2 } from "react-icons/fi";
import { z } from "zod";
import { useSnackbar } from "react-simple-snackbar";

const MAX_ADDITIONAL_MEMBERS: number | undefined = 1;
const EVENT_NAME = "Meta Delhi 2026";
const CTA_LABEL = "Claim your spot";
const siteKey = process.env.NEXT_PUBLIC_API_RECAPTCHA_SITE_KEY || "";
const ReCAPTCHAComponent = ReCAPTCHA as unknown as React.ComponentType<any>;

const getIndustryOptions = (): { label: string; value: string }[] => [
    {
        label: "Arts & Entertainment",
        value: "Arts & Entertainment",
    },
    {
        label: "Business & Industrial",
        value: "Business & Industrial",
    },
    {
        label: "E-Commerce",
        value: "E-Commerce",
    },
    {
        label: "Education",
        value: "Education",
    },
    {
        label: "Electronics",
        value: "Electronics",
    },
    {
        label: "Finance",
        value: "Finance",
    },
    {
        label: "Food & Beverages",
        value: "Food & Beverages",
    },
    {
        label: "Hardware",
        value: "Hardware",
    },
    {
        label: "Health & Beauty",
        value: "Health & Beauty",
    },
    {
        label: "Home & Garden",
        value: "Home & Garden",
    },
    {
        label: "HR Consultants",
        value: "HR Consultants",
    },
    {
        label: "Media",
        value: "Media",
    },
    {
        label: "NGO",
        value: "NGO",
    },
    {
        label: "Real Estate",
        value: "Real Estate",
    },
    {
        label: "Religious & Ceremonial",
        value: "Religious & Ceremonial",
    },
    {
        label: "Software",
        value: "Software",
    },
    {
        label: "Sporting Goods",
        value: "Sporting Goods",
    },
    {
        label: "Tax Consultants",
        value: "Tax Consultants",
    },
    {
        label: "Tours & Travel",
        value: "Tours & Travel",
    },
    {
        label: "Vehicles & Parts",
        value: "Vehicles & Parts",
    },
    {
        label: "Others",
        value: "Others",
    },
];

const industryOptions = getIndustryOptions();

type IndustryOption = (typeof industryOptions)[number];

const requiredTextField = (label: string) => z.string().trim().min(1, `${label} is required`).max(100, `${label} must be 100 characters or fewer`);

const whatsAppNumberSchema = z
    .string()
    .trim()
    .min(1, "WhatsApp number is required")
    .refine((value) => verifyPhoneNumber(value), "Enter a valid WhatsApp number");

const workEmailSchema = z.string().trim().min(1, "Work email address is required").email("Enter a valid work email address");

const industryOptionSchema = z.custom<IndustryOption>(
    (value): value is IndustryOption => {
        if (!value || typeof value !== "object") {
            return false;
        }

        const option = value as Partial<IndustryOption>;

        return (
            typeof option.label === "string" &&
            typeof option.value === "string" &&
            industryOptions.some((industryOption) => industryOption.label === option.label && industryOption.value === option.value)
        );
    },
    { message: "Industry is required" }
);

const memberSchema = z.object({
    fullName: requiredTextField("Full name"),
    designation: requiredTextField("Designation"),
    whatsAppNumber: whatsAppNumberSchema,
    workEmailAddress: workEmailSchema,
    companyName: requiredTextField("Company name"),
});

const eventRegistrationFormSchema = z.object({
    fullName: requiredTextField("Full name"),
    companyName: requiredTextField("Company name"),
    designation: requiredTextField("Designation"),
    industry: industryOptionSchema,
    whatsAppNumber: whatsAppNumberSchema,
    workEmailAddress: workEmailSchema,
    members: z.array(memberSchema),
});

type EventRegistrationFormValues = z.infer<typeof eventRegistrationFormSchema>;

type EventRegistrationSubmitPayload = Omit<EventRegistrationFormValues, "industry"> & {
    industry: string;
    eventName: string;
    recaptchaToken: string;
    track: {
        url: string;
        referrer: string;
        width: number;
        ctaLabel: string;
    };
    tracker?: SourceInformation;
};

interface FormInputProps {
    label: string;
    placeholder: string;
    error?: string;
    type?: React.HTMLInputTypeAttribute;
    registration: UseFormRegisterReturn;
}

interface PhoneFieldProps {
    label: string;
    placeholder: string;
    error?: string;
    value: string;
    name: string;
    onChange: (value: string) => void;
    onBlur: () => void;
}

interface SelectFieldProps {
    label: string;
    placeholder: string;
    error?: string;
    options: IndustryOption[];
    value?: IndustryOption | null;
    name: string;
    onChange: (value: IndustryOption | null) => void;
    onBlur: () => void;
}

const inputClassName =
    "w-full rounded-lg border border-[#E6ECF5] bg-[#FAFCFF] px-4 py-3 text-sm text-gray-900 outline-none transition placeholder:text-[#B8C5DA] focus:border-[#9DB7FF] focus:bg-white";

const phoneInputClassName =
    "w-full rounded-lg border border-[#E6ECF5] bg-[#FAFCFF] py-3 pl-[52px] pr-4 text-sm text-gray-900 outline-none transition placeholder:text-[#B8C5DA] focus:border-[#9DB7FF] focus:bg-white";

const composePhoneNumber = (countryCode: string, phoneNumber: string): string => {
    const trimmedPhoneNumber = phoneNumber.trim();

    if (!trimmedPhoneNumber) {
        return "";
    }

    return `${countryCode}${trimmedPhoneNumber.replace(/^\+/, "")}`;
};

const InputField: React.FC<FormInputProps> = ({ label, placeholder, error, type = "text", registration }) => (
    <div>
        <label className="mb-2 block text-base font-normal text-gray-900">
            {label}
            <span className="text-red-500">*</span>
        </label>
        <input type={type} placeholder={placeholder} className={inputClassName} style={error ? errMsgFocusStyle : undefined} {...registration} />
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
    </div>
);

const SelectField: React.FC<SelectFieldProps> = ({ label, placeholder, error, options, value, name, onChange, onBlur }) => (
    <div>
        <label className="mb-2 block text-base font-normal text-gray-900">
            {label}
            <span className="text-red-500">*</span>
        </label>
        <div className="relative">
            <select
                name={name}
                value={value?.value ?? ""}
                onBlur={onBlur}
                onChange={(event) => {
                    const selectedValue = event.target.value;
                    const selectedOption = options.find((option) => option.value === selectedValue) ?? null;
                    onChange(selectedOption);
                }}
                className={`${inputClassName} appearance-none pr-14`}
                style={{
                    paddingBlock: "0.9rem",
                    color: "#4A5568",
                    ...(error ? errMsgFocusStyle : {}),
                }}
            >
                <option value="" disabled>
                    {placeholder}
                </option>
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            <FiChevronDown className="pointer-events-none absolute right-3 top-5 translate-y-1/2 text-[22px] text-gray-900" />
        </div>
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
    </div>
);

const PhoneField: React.FC<PhoneFieldProps> = ({ label, placeholder, error, value, name, onChange, onBlur }) => {
    const [countryCode, setCountryCode] = useState("+91");
    const [phoneNumber, setPhoneNumber] = useState("");

    useEffect(() => {
        if (!value) {
            setPhoneNumber("");
        }
    }, [value]);

    return (
        <div>
            <label className="mb-2 block text-base font-normal text-gray-900">
                {label}
                <span className="text-red-500">*</span>
            </label>
            <IntlTelInput
                containerClassName="intl-tel-input w-full"
                inputClassName={phoneInputClassName}
                defaultCountry="in"
                value={phoneNumber}
                telInputProps={{
                    name,
                    onBlur,
                    style: error ? ({ ...errMsgFocusStyle, height: "3rem" } as React.CSSProperties) : ({ height: "3rem" } as React.CSSProperties),
                }}
                onSelectFlag={(_currentNumber: string, countryData: CountryData) => {
                    const nextCountryCode = countryData?.dialCode ? `+${countryData.dialCode}` : "+91";
                    setCountryCode(nextCountryCode);
                    onChange(composePhoneNumber(nextCountryCode, phoneNumber));
                }}
                onPhoneNumberChange={(_isValid: boolean, nextValue: string) => {
                    if (verifyPhoneNumber(nextValue)) {
                        setPhoneNumber(nextValue);
                        onChange(composePhoneNumber(countryCode, nextValue));
                    }
                }}
                placeholder={placeholder}
            />
            {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
        </div>
    );
};

const defaultValues = {
    fullName: "",
    companyName: "",
    designation: "",
    industry: undefined,
    whatsAppNumber: "",
    workEmailAddress: "",
    members: [],
};

const EventRegistrationFormSection: React.FC = () => {
    const [, setIsSubmitted] = useState(false);
    const [, setSubmitError] = useState("");
    const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
    const pendingFormValuesRef = useRef<EventRegistrationFormValues | null>(null);
    const {
        register,
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
        watch,
    } = useForm<EventRegistrationFormValues>({
        resolver: zodResolver(eventRegistrationFormSchema),
        defaultValues,
    });

    const [openSuccessSnackbar] = useSnackbar({ style: { backgroundColor: "#10B981" } });
    const [openErrorSnackbar] = useSnackbar({ style: { backgroundColor: "#DC2626" } });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "members",
    });

    const submitRegistration = async (data: EventRegistrationFormValues, recaptchaToken: string) => {
        let tracker: SourceInformation | undefined;

        try {
            tracker = getUtmAndReferrer();
        } catch {
            tracker = undefined;
        }

        const payload: EventRegistrationSubmitPayload = {
            ...data,
            industry: data.industry.value,
            eventName: EVENT_NAME,
            recaptchaToken,
            track: {
                url: window.location.href ?? "",
                referrer: window.document.referrer ?? "",
                width: window.innerWidth ?? 0,
                ctaLabel: CTA_LABEL,
            },
            tracker,
        };

        await axios.post("/api/v1/event-registration/submit", payload);
        setIsSubmittingRequest(false);
    };

    const { reCaptchaRef, triggerReCaptcha, generateToken } = useRecaptcha(
        async (token: string) => {
            const pendingFormValues = pendingFormValuesRef.current;

            if (!pendingFormValues) {
                openErrorSnackbar("Unable to submit your registration right now. Please try again.");
                setIsSubmitted(false);
                setIsSubmittingRequest(false);
                return;
            }

            try {
                await submitRegistration(pendingFormValues, token);
                openSuccessSnackbar("Application submitted! Check your email for details.", 5000);
                setSubmitError("");
                setIsSubmitted(true);
                reset(defaultValues);
            } catch (error) {
                const errorMessage = (error as { response?: { data?: { error?: string } } }).response?.data?.error;
                openErrorSnackbar(errorMessage || "Unable to submit your registration right now. Please try again.");
                setIsSubmitted(false);
            } finally {
                pendingFormValuesRef.current = null;
                setIsSubmittingRequest(false);
            }
        },
        () => {
            openErrorSnackbar("reCAPTCHA verification failed. Please try again.");
            setIsSubmitted(false);
            pendingFormValuesRef.current = null;
            setIsSubmittingRequest(false);
        }
    );

    const canAddMember = MAX_ADDITIONAL_MEMBERS === undefined || fields.length < MAX_ADDITIONAL_MEMBERS;

    const handleAddMember = () => {
        if (!canAddMember) return;

        setIsSubmitted(false);
        setSubmitError("");
        append({
            fullName: "",
            designation: "",
            whatsAppNumber: "",
            workEmailAddress: "",
            companyName: watch("companyName"),
        });
    };

    const onSubmit: SubmitHandler<EventRegistrationFormValues> = async (data) => {
        if (isSubmittingRequest) {
            return;
        }

        setIsSubmitted(false);
        setSubmitError("");
        setIsSubmittingRequest(true);
        pendingFormValuesRef.current = data;

        if (!siteKey) {
            openErrorSnackbar("reCAPTCHA is not configured. Please try again later.");
            pendingFormValuesRef.current = null;
            setIsSubmittingRequest(false);
            return;
        }

        triggerReCaptcha();
    };

    return (
        <section
            id="event-registration-form"
            className="py-14 lg:py-20"
            style={{
                background: "linear-gradient(103.32deg, #EDF4FF 28.71%, #D2DDFF 84.45%, #C7D8FF 136.43%)",
            }}
        >
            <div className={container_classname}>
                <div className="mx-auto max-w-6xl">
                    <h2 className="text-center text-3xl font-semibold text-gray-900 md:text-5xl" style={{ fontFamily: "Degular Display" }}>
                        Book your spot now
                    </h2>

                    <form
                        className="mx-auto mt-10 rounded-[24px] border border-white/70 bg-white px-5 py-6 shadow-[0px_10px_30px_rgba(15,23,42,0.08)] md:px-8 md:py-8"
                        onSubmit={handleSubmit(onSubmit, () => {
                            setIsSubmitted(false);
                            setSubmitError("");
                        })}
                        noValidate
                    >
                        <div className="mb-8 flex flex-wrap items-center gap-3">
                            <span className="rounded-full border border-[#E7EDF7] bg-white  py-1 text-md font-medium font-inter">
                                We&apos;re keeping the first 100 spots free. Once those are gone, it&apos;s ₹2,499 per seat.
                            </span>
                        </div>

                        <div className="grid gap-6 md:grid-cols-2">
                            <InputField label="Full name " placeholder="Enter your first name" error={errors.fullName?.message} registration={register("fullName")} />
                            <InputField label="Company name " placeholder="Enter your company name" error={errors.companyName?.message} registration={register("companyName")} />
                            <InputField label="Designation " placeholder="Enter your designation" error={errors.designation?.message} registration={register("designation")} />
                            <Controller
                                control={control}
                                name="industry"
                                render={({ field }) => (
                                    <SelectField
                                        label="Industry"
                                        placeholder="Select your industry"
                                        error={errors.industry?.message}
                                        options={industryOptions}
                                        value={field.value}
                                        name={field.name}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name="whatsAppNumber"
                                render={({ field }) => (
                                    <PhoneField
                                        label="WhatsApp number "
                                        placeholder="Enter your WhatsApp number"
                                        error={errors.whatsAppNumber?.message}
                                        value={field.value}
                                        name={field.name}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                    />
                                )}
                            />
                            <InputField
                                label="Work email address "
                                placeholder="Enter your work email address"
                                error={errors.workEmailAddress?.message}
                                type="email"
                                registration={register("workEmailAddress")}
                            />
                        </div>

                        <div className="mt-8 space-y-4">
                            {fields.map((field, index) => (
                                <div key={field.id} className="rounded-2xl bg-white p-5 md:p-6" style={{ border: "1px solid rgba(230, 236, 245, 1)" }}>
                                    <div className="mb-5 flex items-start justify-between gap-4">
                                        <h3 className="text-xl font-medium text-gray-900">Additional attendee details</h3>
                                        <button
                                            type="button"
                                            aria-label={`Remove member ${index + 1}`}
                                            className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F3F6FB] text-gray-500 transition hover:text-gray-700"
                                            onClick={() => {
                                                setIsSubmitted(false);
                                                setSubmitError("");
                                                remove(index);
                                            }}
                                        >
                                            <FiTrash2 size={16} className="hover:text-red-500" />
                                        </button>
                                    </div>

                                    <div className="grid gap-6 md:grid-cols-2">
                                        <InputField
                                            label="Full name "
                                            placeholder="Enter your first name"
                                            error={errors.members?.[index]?.fullName?.message}
                                            registration={register(`members.${index}.fullName` as const)}
                                        />
                                        <InputField
                                            label="Designation "
                                            placeholder="Enter your designation"
                                            error={errors.members?.[index]?.designation?.message}
                                            registration={register(`members.${index}.designation` as const)}
                                        />
                                        <Controller
                                            control={control}
                                            name={`members.${index}.whatsAppNumber` as const}
                                            render={({ field: memberPhoneField }) => (
                                                <PhoneField
                                                    label="WhatsApp number "
                                                    placeholder="Enter your WhatsApp number"
                                                    error={errors.members?.[index]?.whatsAppNumber?.message}
                                                    value={memberPhoneField.value}
                                                    name={memberPhoneField.name}
                                                    onChange={memberPhoneField.onChange}
                                                    onBlur={memberPhoneField.onBlur}
                                                />
                                            )}
                                        />
                                        <InputField
                                            label="Work email address "
                                            placeholder="Enter your Work email address"
                                            error={errors.members?.[index]?.workEmailAddress?.message}
                                            type="email"
                                            registration={register(`members.${index}.workEmailAddress` as const)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {canAddMember ? (
                            <div className="mt-4">
                                <button
                                    type="button"
                                    onClick={handleAddMember}
                                    disabled={!canAddMember}
                                    className="inline-flex items-center gap-2 rounded-md px-4 py-4 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60"
                                    style={{ backgroundColor: "rgba(230, 243, 255, 1)", color: "rgba(51, 100, 250, 1)" }}
                                >
                                    <FiPlus size={16} />
                                    Add member
                                </button>
                            </div>
                        ) : null}

                        <p className="mt-8 text-xs leading-relaxed text-black">
                            By registering you agree to receive event updates via WhatsApp and email. Registrations are subject to review — confirmation sent within 24 hours.{" "}
                            information will not be shared with third parties.
                        </p>

                        <div className="mt-8 flex flex-col items-center gap-4">
                            <button
                                type="submit"
                                disabled={isSubmitting || isSubmittingRequest}
                                className="min-w-[220px] rounded-md px-8 py-4 text-lg font-semibold text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70"
                                style={{ backgroundColor: "#3364FA" }}
                            >
                                {isSubmitting || isSubmittingRequest ? "Submitting..." : "Claim your spot"}
                            </button>
                        </div>
                    </form>
                    {siteKey ? <ReCAPTCHAComponent ref={reCaptchaRef} sitekey={siteKey} size="invisible" onChange={generateToken} /> : null}
                </div>
            </div>
        </section>
    );
};

export default EventRegistrationFormSection;
